import styles from "../HealthChat.module.css";
import { useState, useEffect, useRef } from "react";

const UserCheckIcon = ({ size = 52 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="#fff"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
    <polyline points="16 11 18 13 22 9" />
  </svg>
);

const PhoneIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="#94a3b8"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 11.69 19a19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.6 2.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const MailIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="#94a3b8"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const COPY = {
  en: {
    title: "Welcome Back",
    subtitle: "Enter your details to access your appointments",
    emailLabel: "Email Address",
    emailPlaceholder: "Enter your registered email",
    phoneLabel: "Phone Number",
    phonePlaceholder: "Enter your registered phone number",
    orText: "or",
    continueBtn: "Verify OTP",
    backBtn: "Back",
    newPatientLink: "New here? Book as new patient",
  },
  hi: {
    title: "वापस स्वागत है",
    subtitle: "अपने अपॉइंटमेंट देखने के लिए विवरण दर्ज करें",
    emailLabel: "ईमेल पता",
    emailPlaceholder: "अपना पंजीकृत ईमेल दर्ज करें",
    phoneLabel: "फ़ोन नंबर",
    phonePlaceholder: "अपना पंजीकृत फ़ोन नंबर दर्ज करें",
    orText: "या",
    continueBtn: "जारी रखें",
    backBtn: "वापस",
    newPatientLink: "नए हैं? नए रोगी के रूप में बुक करें",
  },
  te: {
    title: "తిరిగి స్వాగతం",
    subtitle: "మీ అపాయింట్మెంట్లు యాక్సెస్ చేయడానికి వివరాలు నమోదు చేయండి",
    emailLabel: "ఇమెయిల్ చిరునామా",
    emailPlaceholder: "మీ నమోదు చేసిన ఇమెయిల్ నమోదు చేయండి",
    phoneLabel: "ఫోన్ నంబర్",
    phonePlaceholder: "మీ నమోదు చేసిన ఫోన్ నంబర్ నమోదు చేయండి",
    orText: "లేదా",
    continueBtn: "కొనసాగించు",
    backBtn: "వెనుకకు",
    newPatientLink: "కొత్తగా వచ్చారా? కొత్త రోగిగా బుక్ చేయండి",
  },
};

export default function ExistingPatientScreen({
  lang = "en",
  onContinue,
  onBack,
  onSwitchNew,
}) {
  const c = COPY[lang] || COPY.en;
  const OTP_LENGTH = 6;
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [error, setError] = useState("");
  const [resent, setResent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState("");
  const [countdown, setCountdown] = useState(60);
  const [otpSent, setOtpSent] = useState(false);
  const inputRefs = useRef([]);

  // Countdown timer — starts at mount, ticks every second
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  useEffect(() => {
    setOtpSent(false);
    setOtp(Array(OTP_LENGTH).fill(""));
  }, [phone]);

  const handleChange = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...otp];
    next[i] = val;
    setOtp(next);
    setError("");
    if (val && i < OTP_LENGTH - 1) inputRefs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace" && !otp[i] && i > 0)
      inputRefs.current[i - 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH)
      .split("");
    const next = [...otp];
    pasted.forEach((d, i) => {
      if (i < OTP_LENGTH) next[i] = d;
    });
    setOtp(next);
    inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleVerify = async () => {
    const code = otp.join("");
    if (code.length < OTP_LENGTH) {
      setError(`Please enter all ${OTP_LENGTH} digits`);
      return;
    }
    setLoading(true);
    // TODO: call your OTP verify API here
    await new Promise((r) => setTimeout(r, 800)); // simulated delay
    setLoading(false);
    //onVerified();
  };

  const handleResend = () => {
    if (countdown > 0) return; // guard — button disabled during countdown
    setOtp(Array(OTP_LENGTH).fill(""));
    setError("");
    setResent(true);
    setCountdown(60); // restart 60s timer
    setTimeout(() => setResent(false), 3000);
    // TODO: call your resend OTP API here
  };

  const validate = () => {
    setError("");

    if (!phone) {
      setError("Please enter phone");
      return false;
    }

    // if (email) {
    //   if (!/^\S+@\S+\.\S+$/.test(email)) {
    //     setError("Enter a valid email");
    //     return false;
    //   }
    // }

    if (phone) {
      if (!/^\d{10}$/.test(phone)) {
        setError("Phone must be 10 digits");
        return false;
      }
    }

    return true;
  };

  const isPhoneValid = /^\d{10}$/.test(phone);
  return (
    <div className={styles.bookingFormScreen}>
      <button className={styles.selectDoctorBack} onClick={onBack}>
        ← Back
      </button>
      {/* Icon */}
      <div className={styles.bookingIconCircle}>
        <UserCheckIcon size={30} />
      </div>
      <div className={`${styles.bookingFormTitle} `}>{c.title}</div>
      <div className={`${styles.bookingFormSub}`}>{c.subtitle}</div>

      {/* <div className={styles.bookingFieldGroup}>
        <label className={`${styles.bookingFieldLabel} ${styles.CBbodyText}`}>
          {c.emailLabel}
        </label>
        <div className={styles.bookingInputWrap}>
          <span className={styles.bookingInputIcon}>
            <MailIcon />
          </span>
          <input
            className={styles.bookingInput}
            placeholder={c.emailPlaceholder}
            type="email"
            disabled={!!phone}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
      </div> */}

      {/* <div className={styles.bookingOrDivider}>{c.orText}</div> */}

      <div className={styles.bookingFieldGroup}>
        <label className={`${styles.bookingFieldLabel} ${styles.CBbodyText}`}>
          {c.phoneLabel}
        </label>

        <div className={styles.bookingInputWrap}>
          <span className={styles.bookingInputIcon}>
            <PhoneIcon />
          </span>
          <span className="ava-text-sm" style={{ color: "#374151" }}>
            +91
          </span>
          <input
            //className={styles.bookingInput}
            className="flex-1  min-w-0 px-3 py-3 text-sm outline-none bg-white"
            placeholder={c.phonePlaceholder}
            type="tel"
            maxLength={10}
            value={phone}
            //  disabled={!!email}
            // onChange={(e) => setPhone(e.target.value)}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, ""); 
              setPhone(value);
            }}
          />
          {isPhoneValid && (
            <button
              className={styles.sendOtpBtn}
              onClick={() => {
                if (!isPhoneValid) {
                  setError("Enter valid phone number");
                  return;
                }
                setOtpSent(true);
                setError("");
                setCountdown(60);
                   setTimeout(() => {
                  inputRefs.current[0]?.focus();
                }, 0);
              }}
            >
              Send OTP
            </button>
          )}
        </div>

        <div >
          {otpSent && (
            <div className="px-4 pt-1.5 pb-2 flex flex-col items-center gap-4">
              <div className="text-center">
                <p className="ava-text-sm">Enter Verification Code</p>
                <p className="ava-text-xs">
                  We sent a {OTP_LENGTH}-digit OTP to{" "}
                  <strong>+91 {phone}</strong>
                </p>
              </div>

              {/* OTP boxes */}
              <div className="flex gap-2 justify-center" onPaste={handlePaste}>
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => (inputRefs.current[i] = el)}
                    type="tel"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    className="text-center font-bold text-lg rounded-md outline-none transition-all"
                    style={{
                      width: 44,
                      height: 40,
                      border: digit
                        ? "2px solid var(--ava-color-primary-500)"
                        : error
                          ? "2px solid var(--ava-color-error-base)"
                          : "2px solid #e5e7eb",
                      backgroundColor: digit
                        ? "var(--ava-color-primary-50)"
                        : "#fff",
                      color: "#1a1a1a",
                    }}
                  />
                ))}
              </div>

              {error && (
                <p
                  className="ava-text-xs text-center"
                  style={{ color: "var(--ava-color-error-base)" }}
                >
                  {error}
                </p>
              )}
              {resent && (
                <p
                  className="ava-text-xs text-center"
                  style={{ color: "#22c55e" }}
                >
                  OTP resent successfully!
                </p>
              )}

              {/* Resend */}
              <p className="ava-text-xs" style={{ color: "var(--text-light)" }}>
                Didn't receive it?{" "}
                {countdown > 0 ? (
                  <span style={{ color: "#9ca3af" }}>
                    Resend in{" "}
                    <strong style={{ color: "var(--primary-color)" }}>
                      {countdown}s
                    </strong>
                  </span>
                ) : (
                  <button
                    onClick={handleResend}
                    className=" cursor-pointer"
                    style={{
                      color: "var(--primary-color)",
                      background: "none",
                      border: "none",
                    }}
                  >
                    Resend OTP
                  </button>
                )}
              </p>
            </div>
          )}
        </div>
      </div>
      {error && <p className={styles.errorText}>{error}</p>}
      <div className={styles.bookingActions}>
        {/* <button className={`${styles.bookingBackBtn} ${styles.CBheading3}`} onClick={onBack}>
          {c.backBtn}
        </button> */}
        {/* <button
          className={`${styles.bookingContinueBtn} ${styles.CBheading3}`}
          // onClick={() => onContinue({ email, phone })}
          onClick={() => {
            if (validate()) {
              onContinue({ email, phone });
            }
          }}
          disabled={!email && !phone}
        >
          {c.continueBtn}
        </button> */}

        {otpSent && (<button
          className={`${styles.bookingContinueBtn} ${styles.CBheading3}`}
          onClick={() => {
            // if (!otpSent) return;
            if (otp.join("").length !== OTP_LENGTH) {
              setError("Enter valid OTP");
              return;
            }
            onContinue({ phone, otp });
          }}
          disabled={!otpSent}
          style={{
            opacity: otp.join("").length !== OTP_LENGTH ? 0.5 : 1,
            cursor:
              otp.join("").length !== OTP_LENGTH
                ? "not-allowed"
                : "pointer",
          }}
        >
          {c.continueBtn}
        </button>)}
      </div>

      <button
        className={`${styles.switchPatientLink} ${styles.CBheading3}`}
        onClick={onSwitchNew}
      >
        {c.newPatientLink}
      </button>
    </div>
  );
}
